export function resolveImage(content, field, mediaFolders = {}) {
    // field is like 'image' which maps to content.image_folder_id, content.image_file_id, content.image_url
    const folderId = content[`${field}_folder_id`];
    const fileId = content[`${field}_file_id`];
    const urlFallback = content[`${field}_url`];

    if (folderId && fileId && mediaFolders[folderId]) {
        const files = mediaFolders[folderId];
        const found = files.find(f => String(f.id) === String(fileId));
        if (found) return found.image_url;
    }

    return urlFallback || null;
}

export function resolveImageData(content, field, mediaFolders = {}) {
    const folderId = content[`${field}_folder_id`];
    const fileId   = content[`${field}_file_id`];
    const urlFallback = content[`${field}_url`];

    if (folderId && fileId && mediaFolders[folderId]) {
        const files = mediaFolders[folderId];
        const found = files.find(f => String(f.id) === String(fileId));
        if (found) return {
            src: found.image_url,
            variantUrls: found.variant_urls || null,
            width: found.width || null,
            height: found.height || null,
        };
    }

    return { src: urlFallback || null, variantUrls: null, width: null, height: null };
}

export function buildSrcSet(fileOrVariantUrls, originalSrc) {
    const vUrls = fileOrVariantUrls?.variant_urls || fileOrVariantUrls;
    const src = originalSrc || fileOrVariantUrls?.image_url;
    if (!vUrls || typeof vUrls !== 'object') return undefined;
    const entries = [];
    for (const key of ['480w','768w','1200w','1920w']) {
        if (vUrls[key]) entries.push(`${vUrls[key]} ${key}`);
    }
    if (src && entries.length) entries.push(`${src} 2400w`);
    return entries.length ? entries.join(', ') : undefined;
}

export function getImageSizes(columns = 1) {
    switch (columns) {
        case 1: return '100vw';
        case 2: return '(max-width:640px) 100vw, 50vw';
        case 3: return '(max-width:640px) 100vw, (max-width:1024px) 50vw, 33vw';
        case 4: return '(max-width:640px) 50vw, (max-width:1024px) 33vw, 25vw';
        case 5: return '(max-width:640px) 50vw, (max-width:1024px) 33vw, 20vw';
        case 6: return '(max-width:640px) 50vw, (max-width:1024px) 33vw, 17vw';
        default: return '100vw';
    }
}

export function isColorDark(hex) {
    if (!hex) return false;
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    if (hex.length !== 6) return false;
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

export function hexToRgba(hex, alpha = 1) {
    if (!hex) return null;
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    if (hex.length !== 6) return null;
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const validAlpha = Math.max(0, Math.min(1, Number(alpha) || 1));
    return `rgba(${r},${g},${b},${validAlpha})`;
}

/**
 * Calculate WCAG 2.1 contrast ratio between two hex colors.
 * Returns ratio (1–21). WCAG AA requires 4.5:1 for normal text, 3:1 for large text.
 */
export function contrastRatio(hex1, hex2) {
    const luminance = (hex) => {
        hex = (hex || '').replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        const rgb = hex.match(/.{2}/g)?.map(c => {
            const v = parseInt(c, 16) / 255;
            return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
        });
        if (!rgb || rgb.length < 3) return 0;
        return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
    };
    const l1 = luminance(hex1);
    const l2 = luminance(hex2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

// ── Background helpers ──────────────────────────────
const GRADIENT_DIRS = {
    'to-b': 'to bottom', 'to-t': 'to top',
    'to-r': 'to right', 'to-l': 'to left',
    'to-br': 'to bottom right', 'to-bl': 'to bottom left',
    'to-tr': 'to top right', 'to-tl': 'to top left',
};

export function buildBackground(config = {}, fallback = null, mediaFolders = {}) {
    const bg = config.bg_color || fallback;

    // Resolve bg_image from mediaFolders or direct URL
    let bgImageUrl = config.bg_image_url || null;
    if (config.bg_image_folder_id && config.bg_image_file_id && mediaFolders[config.bg_image_folder_id]) {
        const found = mediaFolders[config.bg_image_folder_id].find(f => String(f.id) === String(config.bg_image_file_id));
        if (found) bgImageUrl = found.image_url;
    }

    if (config.bg_gradient && config.bg_color && config.bg_gradient_to) {
        const dir = GRADIENT_DIRS[config.bg_gradient_dir] || 'to bottom';
        const style = { background: `linear-gradient(${dir}, ${config.bg_color}, ${config.bg_gradient_to})` };
        if (bgImageUrl) {
            const overlay = (config.bg_image_overlay ?? 50) / 100;
            style.background = `linear-gradient(rgba(0,0,0,${overlay}),rgba(0,0,0,${overlay})), url(${bgImageUrl}), ${style.background}`;
            style.backgroundSize = 'cover';
            style.backgroundPosition = 'center';
        }
        return style;
    }
    if (config.bg_pattern && bg) {
        const patterns = {
            dots: `radial-gradient(circle, ${hexToRgba(config.accent_color || '#000', 0.06)} 1px, transparent 1px)`,
            grid: `linear-gradient(${hexToRgba(config.accent_color || '#000', 0.04)} 1px, transparent 1px), linear-gradient(90deg, ${hexToRgba(config.accent_color || '#000', 0.04)} 1px, transparent 1px)`,
            diagonals: `repeating-linear-gradient(45deg, transparent, transparent 10px, ${hexToRgba(config.accent_color || '#000', 0.03)} 10px, ${hexToRgba(config.accent_color || '#000', 0.03)} 11px)`,
        };
        const pat = patterns[config.bg_pattern];
        if (pat) {
            return {
                backgroundColor: bg,
                backgroundImage: pat,
                ...(config.bg_pattern === 'dots' ? { backgroundSize: '20px 20px' } :
                    config.bg_pattern === 'grid' ? { backgroundSize: '30px 30px' } : {}),
            };
        }
    }
    if (bgImageUrl) {
        const overlay = (config.bg_image_overlay ?? 50) / 100;
        return {
            background: `linear-gradient(rgba(0,0,0,${overlay}),rgba(0,0,0,${overlay})), url(${bgImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: bg || undefined,
        };
    }
    return bg ? { backgroundColor: bg } : {};
}

// ── Section styles ──────────────────────────────────
export function sectionStyles(config = {}, primaryColor = '#2563eb', secondaryColor = null, mediaFolders = {}) {
    const hasBgImage = !!(config.bg_image_url || config.bg_image_folder_id);
    const dark = hasBgImage
        ? (config.bg_image_overlay ?? 50) >= 40
        : config.bg_gradient
            ? isColorDark(config.bg_color) || isColorDark(config.bg_gradient_to)
            : isColorDark(config.bg_color);
    const customText = config.text_color;
    const bgStyle = buildBackground(config, null, mediaFolders);
    if (config.bg_animation === 'gradient-shift' && config.bg_gradient) {
        bgStyle.backgroundSize = '200% 200%';
        bgStyle.animation = 'lmBgGradShift 6s ease infinite';
    }
    return {
        bg: config.bg_color || null,
        bgStyle,
        text: customText || (dark ? '#ffffff' : '#111827'),
        textSub: customText
            ? hexToRgba(customText, 0.7)
            : (dark ? 'rgba(255,255,255,0.65)' : '#6b7280'),
        accent: config.accent_color || primaryColor,
        secondary: secondaryColor || config.accent_color || primaryColor,
        dark,
        padClass: config.padding === 'sm' ? 'py-12 md:py-16'
            : config.padding === 'lg' ? 'py-24 md:py-32'
            : 'py-16 md:py-24',
        maxWidth: config.max_width === 'narrow' ? 'max-w-4xl'
            : config.max_width === 'wide' ? 'max-w-screen-2xl'
            : config.max_width === 'full' ? 'max-w-full'
            : 'max-w-7xl',
        radiusClass: config.section_radius === 'sm' ? 'rounded-xl mx-4 md:mx-8'
            : config.section_radius === 'lg' ? 'rounded-2xl mx-4 md:mx-8'
            : config.section_radius === 'xl' ? 'rounded-3xl mx-4 md:mx-12'
            : '',
        shadowClass: config.section_shadow === 'sm' ? 'shadow-md'
            : config.section_shadow === 'lg' ? 'shadow-2xl'
            : config.section_shadow === 'glow' ? 'shadow-[0_0_60px_rgba(0,0,0,0.08)]'
            : '',
    };
}

// ── Card styles ─────────────────────────────────────
export function getCardClass(style = 'shadow', darkBg = false) {
    switch (style) {
        case 'border':
            return 'bg-white rounded-2xl border-2 border-gray-200 hover:border-gray-300 transition-all duration-300';
        case 'flat':
            return darkBg
                ? 'bg-white/10 rounded-2xl hover:bg-white/15 transition-all duration-300'
                : 'bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all duration-300';
        case 'glass':
            return darkBg
                ? 'bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300'
                : 'bg-white/70 backdrop-blur-md rounded-2xl border border-gray-200/50 hover:bg-white/90 transition-all duration-300';
        case 'glow':
            return darkBg
                ? 'bg-white/10 rounded-2xl border border-white/10 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-all duration-300'
                : 'bg-white rounded-2xl border border-gray-100 hover:shadow-[0_0_30px_rgba(99,102,241,0.15)] transition-all duration-300';
        case 'gradient-border':
            return darkBg
                ? 'bg-white/5 rounded-2xl ring-1 ring-white/20 hover:ring-white/40 transition-all duration-300'
                : 'bg-white rounded-2xl ring-1 ring-gray-200 hover:ring-indigo-300 transition-all duration-300';
        case 'minimal':
            return darkBg
                ? 'rounded-2xl hover:bg-white/5 transition-all duration-300'
                : 'rounded-2xl hover:bg-gray-50 transition-all duration-300';
        case 'tilt':
            return darkBg
                ? 'bg-white/10 rounded-2xl border border-white/20 hover:[transform:perspective(600px)_rotateY(-3deg)_rotateX(3deg)] transition-all duration-300'
                : 'bg-white rounded-2xl shadow-sm border border-gray-100 hover:[transform:perspective(600px)_rotateY(-3deg)_rotateX(3deg)] hover:shadow-lg transition-all duration-300';
        case 'neumorphism':
            return darkBg
                ? 'rounded-2xl hover:shadow-lg transition-all duration-300'
                : 'bg-gray-100 rounded-2xl shadow-[6px_6px_12px_#d1d5db,-6px_-6px_12px_#ffffff] hover:shadow-[8px_8px_16px_#d1d5db,-8px_-8px_16px_#ffffff] transition-all duration-300';
        case 'brutalism':
            return darkBg
                ? 'bg-white/10 rounded-none border-2 border-white/30 shadow-[4px_4px_0_rgba(255,255,255,0.2)] hover:shadow-[6px_6px_0_rgba(255,255,255,0.3)] transition-all duration-300'
                : 'bg-white rounded-none border-2 border-gray-900 shadow-[4px_4px_0_#111827] hover:shadow-[6px_6px_0_#111827] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all duration-300';
        case 'retro':
            return darkBg
                ? 'bg-white/10 rounded-xl border border-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] hover:bg-white/15 transition-all duration-300'
                : 'bg-amber-50 rounded-xl border-2 border-amber-200 shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-300';
        case 'outlined':
            return darkBg
                ? 'bg-transparent rounded-2xl border-2 border-white/20 hover:border-white/50 hover:bg-white/5 transition-all duration-300'
                : 'bg-transparent rounded-2xl border-2 border-gray-300 hover:border-gray-500 hover:shadow-sm transition-all duration-300';
        default: // shadow
            return 'bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300';
    }
}

export function isCardTransparent(style, darkBg) {
    return style === 'glass' || style === 'glow' || style === 'minimal' || style === 'tilt' || (style === 'flat' && darkBg);
}

// ── Animations ──────────────────────────────────────
export const ANIMATION_TYPES = {
    'fade-up': { hidden: 'opacity-0 translate-y-10', visible: 'opacity-100 translate-y-0' },
    'fade-down': { hidden: 'opacity-0 -translate-y-10', visible: 'opacity-100 translate-y-0' },
    'slide-left': { hidden: 'opacity-0 translate-x-10', visible: 'opacity-100 translate-x-0' },
    'slide-right': { hidden: 'opacity-0 -translate-x-10', visible: 'opacity-100 translate-x-0' },
    'zoom-in': { hidden: 'opacity-0 scale-90', visible: 'opacity-100 scale-100' },
    'blur-in': { hidden: 'opacity-0 blur-sm', visible: 'opacity-100 blur-0' },
    'clip-up': { hidden: 'opacity-0 [clip-path:inset(100%_0_0_0)]', visible: 'opacity-100 [clip-path:inset(0_0_0_0)]' },
    'scale-fade': { hidden: 'opacity-0 scale-[0.8]', visible: 'opacity-100 scale-100' },
    'rotate-in': { hidden: 'opacity-0 rotate-3', visible: 'opacity-100 rotate-0' },
    'flip-in': { hidden: 'opacity-0 [transform:rotateX(90deg)]', visible: 'opacity-100 [transform:rotateX(0deg)]' },
    'bounce-in': { hidden: 'opacity-0 scale-50', visible: 'opacity-100 scale-100 animate-[bounceIn_0.6s]' },
    'swing': { hidden: 'opacity-0 [transform:rotate(-10deg)]', visible: 'opacity-100 [transform:rotate(0deg)]' },
    'slide-up-fade': { hidden: 'opacity-0 translate-y-20', visible: 'opacity-100 translate-y-0' },
    'scale-up': { hidden: 'opacity-0 scale-75', visible: 'opacity-100 scale-100' },
    'drop-in': { hidden: 'opacity-0 -translate-y-20', visible: 'opacity-100 translate-y-0' },
};

export function getAnimation(config = {}, visible = true) {
    const type = config.animation || 'fade-up';
    const anim = ANIMATION_TYPES[type] || ANIMATION_TYPES['fade-up'];
    return `transition-all duration-700 ${visible ? anim.visible : anim.hidden}`;
}

// ── Title styles ────────────────────────────────────
export function getTitleClass(config = {}) {
    const sizes = { normal: 'text-3xl md:text-4xl lg:text-5xl', large: 'text-4xl md:text-5xl lg:text-6xl', xl: 'text-5xl md:text-6xl lg:text-7xl' };
    return sizes[config.title_size] || sizes.normal;
}

export function getTitleStyle(config = {}, accentColor) {
    if (config.title_style === 'gradient' && accentColor) {
        return {
            backgroundImage: `linear-gradient(135deg, ${accentColor}, ${config.bg_gradient_to || hexToRgba(accentColor, 0.87) || accentColor})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        };
    }
    if (config.title_style === 'outlined') {
        return {
            WebkitTextStroke: '2px currentColor',
            WebkitTextFillColor: 'transparent',
        };
    }
    return {};
}

export function getTextShadow(preset, color) {
    switch (preset) {
        case 'sm': return '0 1px 2px rgba(0,0,0,0.15)';
        case 'md': return '0 2px 8px rgba(0,0,0,0.25)';
        case 'lg': return '0 4px 16px rgba(0,0,0,0.4)';
        case 'glow': return color ? `0 0 20px ${hexToRgba(color, 0.5)}, 0 0 40px ${hexToRgba(color, 0.25)}` : '0 0 20px rgba(99,102,241,0.5)';
        default: return 'none';
    }
}

// TITLE_DECORATIONS is in SectionHeader.jsx (needs JSX)

// ── Dividers ────────────────────────────────────────
export const DIVIDER_PATHS = {
    wave: 'M0,64 C320,120 640,0 960,64 C1280,128 1440,32 1440,32 L1440,0 L0,0 Z',
    'wave-smooth': 'M0,48 C360,96 720,0 1080,48 C1260,72 1380,24 1440,32 L1440,0 L0,0 Z',
    angle: 'M0,64 L1440,0 L1440,0 L0,0 Z',
    curve: 'M0,64 Q720,128 1440,0 L1440,0 L0,0 Z',
    zigzag: 'M0,32 L120,64 L240,32 L360,64 L480,32 L600,64 L720,32 L840,64 L960,32 L1080,64 L1200,32 L1320,64 L1440,32 L1440,0 L0,0 Z',
    'mountain': 'M0,64 L360,20 L540,50 L720,5 L900,40 L1080,15 L1260,45 L1440,10 L1440,0 L0,0 Z',
    'multi-wave': 'M0,32 C160,64 320,0 480,32 C640,64 800,0 960,32 C1120,64 1280,0 1440,32 L1440,0 L0,0 Z',
    'cloud': 'M0,40 Q120,80 240,40 Q360,0 480,40 Q600,80 720,40 Q840,0 960,40 Q1080,80 1200,40 Q1320,0 1440,40 L1440,0 L0,0 Z',
    'peaks': 'M0,64 L180,10 L360,64 L540,10 L720,64 L900,10 L1080,64 L1260,10 L1440,64 L1440,0 L0,0 Z',
    'steps': 'M0,60 L240,60 L240,40 L480,40 L480,20 L720,20 L720,40 L960,40 L960,60 L1200,60 L1200,20 L1440,20 L1440,0 L0,0 Z',
    'slant-double': 'M0,40 L720,64 L720,20 L1440,44 L1440,0 L0,0 Z',
    'arrow': 'M0,64 L720,0 L1440,64 L1440,0 L0,0 Z',
};

// ── Option lists ────────────────────────────────────
export const FONT_OPTIONS = [
    { value: '', label: 'Por defecto (System)' },
    // Sans-serif
    { value: 'Inter', label: 'Inter' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Raleway', label: 'Raleway' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'DM Sans', label: 'DM Sans' },
    { value: 'Work Sans', label: 'Work Sans' },
    { value: 'Rubik', label: 'Rubik' },
    { value: 'Manrope', label: 'Manrope' },
    { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
    { value: 'Outfit', label: 'Outfit' },
    { value: 'Space Grotesk', label: 'Space Grotesk' },
    { value: 'Sora', label: 'Sora' },
    { value: 'Figtree', label: 'Figtree' },
    { value: 'Albert Sans', label: 'Albert Sans' },
    { value: 'Lexend', label: 'Lexend' },
    { value: 'Mulish', label: 'Mulish' },
    { value: 'Cabin', label: 'Cabin' },
    { value: 'Karla', label: 'Karla' },
    { value: 'Source Sans 3', label: 'Source Sans 3' },
    { value: 'Quicksand', label: 'Quicksand' },
    { value: 'Barlow', label: 'Barlow' },
    { value: 'Josefin Sans', label: 'Josefin Sans' },
    { value: 'Exo 2', label: 'Exo 2' },
    { value: 'Urbanist', label: 'Urbanist' },
    { value: 'Red Hat Display', label: 'Red Hat Display' },
    { value: 'Archivo', label: 'Archivo' },
    { value: 'IBM Plex Sans', label: 'IBM Plex Sans' },
    { value: 'Overpass', label: 'Overpass' },
    { value: 'Titillium Web', label: 'Titillium Web' },
    { value: 'Nunito Sans', label: 'Nunito Sans' },
    { value: 'Catamaran', label: 'Catamaran' },
    // Serif
    { value: 'Playfair Display', label: 'Playfair Display' },
    { value: 'Merriweather', label: 'Merriweather' },
    { value: 'Lora', label: 'Lora' },
    { value: 'Source Serif 4', label: 'Source Serif 4' },
    { value: 'Cormorant Garamond', label: 'Cormorant Garamond' },
    { value: 'Libre Baskerville', label: 'Libre Baskerville' },
    { value: 'DM Serif Display', label: 'DM Serif Display' },
    { value: 'Crimson Text', label: 'Crimson Text' },
    { value: 'EB Garamond', label: 'EB Garamond' },
    { value: 'Bitter', label: 'Bitter' },
    { value: 'Fraunces', label: 'Fraunces' },
    // Display
    { value: 'Bebas Neue', label: 'Bebas Neue' },
    { value: 'Oswald', label: 'Oswald' },
    { value: 'Righteous', label: 'Righteous' },
    { value: 'Abril Fatface', label: 'Abril Fatface' },
    { value: 'Pacifico', label: 'Pacifico' },
    { value: 'Permanent Marker', label: 'Permanent Marker' },
    { value: 'Comfortaa', label: 'Comfortaa' },
];

export const CARD_STYLE_OPTIONS = [
    { value: 'shadow', label: 'Sombra (default)' },
    { value: 'border', label: 'Borde' },
    { value: 'flat', label: 'Plano' },
    { value: 'glass', label: 'Cristal (Glass)' },
    { value: 'glow', label: 'Resplandor (Glow)' },
    { value: 'gradient-border', label: 'Borde gradiente' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'tilt', label: 'Tilt (3D)' },
    { value: 'neumorphism', label: 'Neumorfismo' },
    { value: 'brutalism', label: 'Brutalismo' },
    { value: 'retro', label: 'Retro' },
    { value: 'outlined', label: 'Contorno' },
];

export const ANIMATION_OPTIONS = [
    { value: 'fade-up', label: 'Fade up' },
    { value: 'fade-down', label: 'Fade down' },
    { value: 'slide-left', label: 'Slide desde derecha' },
    { value: 'slide-right', label: 'Slide desde izquierda' },
    { value: 'zoom-in', label: 'Zoom in' },
    { value: 'blur-in', label: 'Blur in' },
    { value: 'clip-up', label: 'Clip up' },
    { value: 'scale-fade', label: 'Scale fade' },
    { value: 'rotate-in', label: 'Rotate in' },
    { value: 'flip-in', label: 'Flip in' },
    { value: 'bounce-in', label: 'Bounce in' },
    { value: 'swing', label: 'Swing' },
    { value: 'slide-up-fade', label: 'Slide up (largo)' },
    { value: 'scale-up', label: 'Scale up' },
    { value: 'drop-in', label: 'Drop in' },
];

export const DIVIDER_OPTIONS = [
    { value: '', label: 'Ninguno (recto)' },
    { value: 'wave', label: 'Onda' },
    { value: 'wave-smooth', label: 'Onda suave' },
    { value: 'angle', label: 'Diagonal' },
    { value: 'curve', label: 'Arco' },
    { value: 'zigzag', label: 'Zigzag' },
    { value: 'mountain', label: 'Montana' },
    { value: 'multi-wave', label: 'Multi onda' },
    { value: 'cloud', label: 'Nube' },
    { value: 'peaks', label: 'Picos' },
    { value: 'steps', label: 'Escalones' },
    { value: 'slant-double', label: 'Doble diagonal' },
    { value: 'arrow', label: 'Flecha' },
];

export const BG_PATTERN_OPTIONS = [
    { value: '', label: 'Ninguno' },
    { value: 'dots', label: 'Puntos' },
    { value: 'grid', label: 'Cuadricula' },
    { value: 'diagonals', label: 'Diagonales' },
];

export const GRADIENT_DIR_OPTIONS = [
    { value: 'to-b', label: 'Arriba a abajo' },
    { value: 'to-r', label: 'Izquierda a derecha' },
    { value: 'to-br', label: 'Diagonal (abajo-der)' },
    { value: 'to-bl', label: 'Diagonal (abajo-izq)' },
    { value: 'to-tr', label: 'Diagonal (arriba-der)' },
];

export const TITLE_SIZE_OPTIONS = [
    { value: 'normal', label: 'Normal' },
    { value: 'large', label: 'Grande' },
    { value: 'xl', label: 'Extra grande' },
];

export const TITLE_STYLE_OPTIONS = [
    { value: '', label: 'Normal' },
    { value: 'gradient', label: 'Gradiente' },
    { value: 'outlined', label: 'Contorno' },
];

export const TITLE_DECORATION_OPTIONS = [
    { value: 'line', label: 'Linea' },
    { value: 'dots', label: 'Puntos' },
    { value: 'icon', label: 'Estrella' },
    { value: 'none', label: 'Ninguna' },
];

// ── Smart Link helpers ──────────────────────────────
export function resolveLinkProps(href) {
    if (!href || href === '#') return { href: '#' };
    // Block dangerous protocols
    const lower = href.trim().toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) return { href: '#' };
    // Download link
    if (href.startsWith('download:')) {
        const actualUrl = href.slice('download:'.length);
        if (!actualUrl.startsWith('http')) return { href: '#' };
        return { href: actualUrl, download: '', target: '_blank', rel: 'noopener noreferrer' };
    }
    if (href.startsWith('#')) {
        return {
            href,
            onClick: (e) => {
                e.preventDefault();
                const id = href.slice(1);
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
            },
        };
    }
    if (href.startsWith('http') || href.startsWith('//')) {
        return { href, target: '_blank', rel: 'noopener noreferrer' };
    }
    if (href.startsWith('mailto:') || href.startsWith('tel:')) {
        return { href };
    }
    // relative internal link (page slug, etc.)
    return { href };
}

export function inferLinkType(href) {
    if (!href) return 'none';
    if (href.startsWith('download:')) return 'download';
    if (href.startsWith('#')) return 'anchor';
    if (href.startsWith('https://wa.me')) return 'whatsapp';
    if (href.startsWith('tel:')) return 'phone';
    if (href.startsWith('mailto:')) return 'email';
    if (href.startsWith('http') || href.startsWith('//')) return 'url';
    if (href.startsWith('/') || (!href.includes('://') && !href.startsWith('#'))) return 'page';
    return 'url';
}

export const LINK_TYPE_OPTIONS = [
    { value: 'anchor', label: 'Ancla (scroll a seccion)' },
    { value: 'page', label: 'Pagina interna' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'phone', label: 'Telefono' },
    { value: 'email', label: 'Email' },
    { value: 'url', label: 'URL externa' },
    { value: 'download', label: 'Descarga de archivo' },
    { value: 'none', label: 'Sin accion' },
];

// ── Video resolver ──────────────────────────────
export function resolveVideo(obj = {}, prefix = 'video', mediaFolders = {}) {
    // File-based video (from media library)
    if (obj[prefix + '_source'] === 'file') {
        const fid = obj[prefix + '_folder_id'];
        const fileId = obj[prefix + '_file_id'];
        if (fid && fileId && mediaFolders[fid]) {
            const f = mediaFolders[fid].find(x => String(x.id) === String(fileId));
            if (f) return { type: 'file', src: f.image_url, mime: f.mime_type };
        }
        return null;
    }
    // URL-based video (YouTube/Vimeo)
    const url = obj[prefix + '_url'] || obj.url;
    if (!url) return null;
    let match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) return { type: 'youtube', id: match[1], embed: `https://www.youtube.com/embed/${match[1]}`, thumb: `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg` };
    match = url.match(/vimeo\.com\/(\d+)/);
    if (match) return { type: 'vimeo', id: match[1], embed: `https://player.vimeo.com/video/${match[1]}`, thumb: null };
    return null;
}

// ── Download link resolver (pre-process blocks for public rendering) ──
export function resolveDownloadLinks(blocks, mediaFolders = {}) {
    const resolve = (val) => {
        if (typeof val !== 'string' || !val.startsWith('download:')) return val;
        const rest = val.slice('download:'.length);
        if (rest.startsWith('http')) return val;
        const parts = rest.split(':');
        if (parts.length === 2) {
            const files = mediaFolders[parseInt(parts[0])];
            const file = files?.find(f => String(f.id) === parts[1]);
            if (file) return 'download:' + file.image_url;
        }
        return val;
    };
    const walk = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(walk);
        const out = {};
        for (const [k, v] of Object.entries(obj)) {
            out[k] = typeof v === 'string' ? resolve(v) : walk(v);
        }
        return out;
    };
    return blocks.map(b => ({ ...b, content: walk(b.content), config: walk(b.config) }));
}

// ── Subtitle size options ──
export const SUBTITLE_SIZE_OPTIONS = [
    { value: 'sm', label: 'Pequeno' },
    { value: 'normal', label: 'Normal' },
    { value: 'lg', label: 'Grande' },
];

// ── Letter spacing options ──
export const LETTER_SPACING_OPTIONS = [
    { value: 'tight', label: 'Compacto' },
    { value: 'normal', label: 'Normal' },
    { value: 'wide', label: 'Amplio' },
    { value: 'wider', label: 'Muy amplio' },
];

// ── Gap size options ──
export const GAP_SIZE_OPTIONS = [
    { value: 'tight', label: 'Compacto' },
    { value: 'normal', label: 'Normal' },
    { value: 'loose', label: 'Amplio' },
    { value: 'extra', label: 'Extra amplio' },
];

// ── Text shadow presets ──
export const TEXT_SHADOW_OPTIONS = [
    { value: '', label: 'Ninguna' },
    { value: 'sm', label: 'Sutil' },
    { value: 'md', label: 'Media' },
    { value: 'lg', label: 'Fuerte' },
    { value: 'glow', label: 'Resplandor' },
];

// ── Decoration types (background ornaments) ──
export const DECORATION_OPTIONS = [
    { value: '', label: 'Ninguna' },
    { value: 'grain', label: 'Textura grano' },
    { value: 'blur-spots', label: 'Manchas difuminadas' },
    { value: 'grid-dots', label: 'Puntos de grilla' },
    { value: 'gradient-orbs', label: 'Orbes gradiente' },
    { value: 'geometric', label: 'Formas geometricas' },
    { value: 'circles', label: 'Circulos decorativos' },
    { value: 'floating-cards', label: 'Tarjetas flotantes' },
    { value: 'particles', label: 'Particulas animadas' },
];

// ── Hover effect options for cards ──
export const CARD_HOVER_OPTIONS = [
    { value: '', label: 'Default' },
    { value: 'lift', label: 'Elevar' },
    { value: 'grow', label: 'Crecer' },
    { value: 'glow-pulse', label: 'Pulso luminoso' },
    { value: 'border-animate', label: 'Borde animado' },
    { value: 'tilt-3d', label: 'Tilt 3D' },
    { value: 'cursor-glow', label: 'Glow cursor' },
];

// ── Section overlap options ──
export const OVERLAP_OPTIONS = [
    { value: '', label: 'Ninguno' },
    { value: 'sm', label: 'Pequeno (-2rem)' },
    { value: 'md', label: 'Medio (-4rem)' },
    { value: 'lg', label: 'Grande (-6rem)' },
];

// ── Responsive visibility ──
export const VISIBILITY_OPTIONS = [
    { value: '', label: 'Todos los dispositivos' },
    { value: 'desktop-only', label: 'Solo escritorio' },
    { value: 'mobile-only', label: 'Solo movil' },
];

// ── Stagger delay helper ──
export function getStaggerDelay(index, enabled = false, baseDelay = 100) {
    if (!enabled) return '0ms';
    return `${index * baseDelay}ms`;
}

// ── Gap class helper ──
export function getGapClass(size = 'normal') {
    switch (size) {
        case 'tight': return 'gap-3 md:gap-4';
        case 'loose': return 'gap-8 md:gap-10';
        case 'extra': return 'gap-10 md:gap-14';
        default: return 'gap-6 md:gap-8';
    }
}

// ── Letter spacing class helper ──
export function getLetterSpacingClass(spacing) {
    switch (spacing) {
        case 'tight': return 'tracking-tight';
        case 'wide': return 'tracking-wide';
        case 'wider': return 'tracking-[0.15em]';
        default: return '';
    }
}

// ── Subtitle size class helper ──
export function getSubtitleClass(size) {
    switch (size) {
        case 'sm': return 'text-sm md:text-base';
        case 'lg': return 'text-lg md:text-xl';
        default: return 'text-base md:text-lg';
    }
}

// ── Card hover class helper ──
export function getCardHoverClass(effect, accentColor) {
    switch (effect) {
        case 'lift': return 'hover:-translate-y-2 hover:shadow-xl';
        case 'grow': return 'hover:scale-[1.03]';
        case 'glow-pulse': return 'hover:shadow-[0_0_25px_rgba(99,102,241,0.2)]';
        case 'border-animate': return 'hover:ring-2 hover:ring-offset-2';
        case 'tilt-3d': return 'hover:[transform:perspective(600px)_rotateY(-3deg)_rotateX(3deg)]';
        case 'cursor-glow': return '';
        default: return '';
    }
}

// ── Cursor glow effect handlers (DOM-based, no React state) ──
export function cursorGlowHandlers(accentColor) {
    return {
        onMouseMove: (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            e.currentTarget.style.setProperty('--glow-x', `${x}px`);
            e.currentTarget.style.setProperty('--glow-y', `${y}px`);
            e.currentTarget.style.setProperty('--glow-opacity', '1');
        },
        onMouseLeave: (e) => {
            e.currentTarget.style.setProperty('--glow-opacity', '0');
        },
    };
}

// ── CSS for cursor-glow card effect (inject once via <style>) ──
export const CURSOR_GLOW_CSS = `[style*="--glow-x"]::before{content:'';position:absolute;inset:0;background:radial-gradient(circle 150px at var(--glow-x) var(--glow-y),currentColor,transparent 70%);opacity:calc(var(--glow-opacity,0)*0.07);pointer-events:none;transition:opacity 0.3s;border-radius:inherit}`;

// ── Per-section card/grid customization helpers ──

export const CARD_RADIUS_MAP = { none: 'rounded-none', sm: 'rounded-sm', md: 'rounded-lg', lg: 'rounded-xl', xl: 'rounded-2xl', full: 'rounded-3xl' };
export const CARD_PADDING_MAP = { sm: 'p-3 md:p-4', md: 'p-4 md:p-6', lg: 'p-6 md:p-8' };
export const CARD_GAP_MAP = { tight: 'gap-2 md:gap-3', normal: 'gap-4 md:gap-6', wide: 'gap-6 md:gap-8' };
export const ICON_SIZE_MAP = { sm: 'w-8 h-8 text-lg', md: 'w-12 h-12 text-2xl', lg: 'w-16 h-16 text-3xl', xl: 'w-20 h-20 text-4xl' };
export const BUTTON_SIZE_MAP = { sm: 'px-4 py-2 text-xs', md: 'px-6 py-3 text-sm', lg: 'px-10 py-4 text-base' };
export const ANIM_SPEED_MAP = { slow: 'duration-1000', normal: 'duration-700', fast: 'duration-300' };
export const TEXT_ALIGN_MAP = { left: 'text-left', center: 'text-center', right: 'text-right' };

export function getCardRadius(config) { return CARD_RADIUS_MAP[config?.card_radius] || ''; }
export function getCardPadding(config) { return CARD_PADDING_MAP[config?.card_padding] || ''; }
export function getCardGap(config) { return CARD_GAP_MAP[config?.card_gap] || ''; }
export function getIconSize(config) { return ICON_SIZE_MAP[config?.icon_size] || ''; }
export function getButtonSize(config) { return BUTTON_SIZE_MAP[config?.button_size] || ''; }
export function getAnimSpeed(config) { return ANIM_SPEED_MAP[config?.animation_speed] || ''; }
export function getTextAlign(config) { return TEXT_ALIGN_MAP[config?.text_align] || ''; }

export function getGridCols(config) {
    const cols = config?.card_columns;
    if (!cols || cols === 'auto') return '';
    const map = { '1': 'grid-cols-1', '2': 'grid-cols-1 sm:grid-cols-2', '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4', '5': 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' };
    return map[String(cols)] || '';
}

/**
 * Find a media file by ID in a folder, handling number/string type mismatches.
 * @param {Object} mediaFolders - The mediaFolders map
 * @param {string|number} folderId - The folder ID
 * @param {string|number} fileId - The file ID to find
 * @returns {Object|null} The found media file object or null
 */
export function resolveMediaFile(mediaFolders, folderId, fileId) {
    if (!folderId || !fileId || !mediaFolders?.[folderId]) return null;
    return mediaFolders[folderId].find(f => String(f.id) === String(fileId)) || null;
}
